<template>
  <div
    class="color-tile"
    :style="tileStyles"
    :class="{
      'color-tile--large': size === 'large',
      'color-tile--empty': isEmptyOrInvalid,
      'color-tile--transparent': color === 'transparent',
      'color-tile--with-border': shouldShowBorder,
    }"
  >
    <Icon
      v-if="color === 'transparent'"
      class="color-tile__transparent-icon"
      name="transparent"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'ColorTile',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import { getSaturatedColor, isValidHexColor, ensureHexPrefix } from '@/uiKit/colorPalette'

const props = withDefaults(
  defineProps<{
    color: string
    size?: 'default' | 'large'
    borderColor?: string
  }>(),
  {
    size: 'default',
    borderColor: undefined,
  },
)

const isHexColor = computed(() => !!props.color && isValidHexColor(props.color))

const isEmptyOrInvalid = computed(
  () => !props.color || (props.color.startsWith('#') && !isHexColor.value),
)

const borderColorValue = computed(() => {
  if (props.borderColor) {
    return props.borderColor
  }

  if (isHexColor.value) {
    return getSaturatedColor(ensureHexPrefix(props.color))
  }

  return undefined
})

const shouldShowBorder = computed(
  () =>
    borderColorValue.value !== undefined &&
    !isEmptyOrInvalid.value &&
    props.color !== 'transparent',
)

const tileStyles = computed(() => {
  if (props.color === 'transparent') {
    return {
      background: 'transparent',
    }
  }

  // For empty or invalid input, show placeholder style
  if (isEmptyOrInvalid.value) {
    return {
      background: 'var(--color-surface-secondary)',
      color: 'var(--color-border-default)',
    }
  }

  return {
    background: isHexColor.value ? ensureHexPrefix(props.color) : props.color,
    color: borderColorValue.value,
  }
})
</script>

<style lang="scss" scoped>
@import 'colorPicker';

.color-tile {
  @include colorIcon;
  border-width: 1px;

  &--empty {
    border-style: dashed;
  }

  &--with-border {
    border-style: solid;
    border-width: 1px;
  }

  &--large {
    width: $s-2xl;
    height: $s-2xl;
  }

  &__transparent-icon {
    font-size: inherit;
  }
}
</style>
